/**
 * 鼠标事件适配器：按"远程输入协议 v2"将本地鼠标行为映射到远端。
 *
 * 协议规则：docs/reference/protocol/remote-input.md
 *
 * 通道分发：
 *   - `mouseMove` / `mouseMoveRelative` → cursor 通道（无序、不重传）
 *   - `mouseDown` / `mouseUp` / `mouseWheel` → control 通道（可靠有序）
 *
 * 所有 cursor 通道消息携带 sender 单调递增的 `seq`，receiver 用于丢弃旧帧。
 * 所有边沿/滚轮消息携带事件发生时的 `x/y`，receiver 在执行注入前先校准位置。
 */
import throttle from "lodash.throttle";
import { useDeviceStore } from "@/stores/remote";
import { hasNegotiatedCapability } from "./negotiation";

const PROTOCOL_VERSION = 2 as const;

const IS_DEV = import.meta.env.DEV;

/**
 * sender 在本次会话内对**所有 cursor 通道消息**维护的统一单调递增计数器。
 * 协议 §7.1 要求 `mouseMove` 据此丢弃乱序旧帧；`mouseMoveRelative` 不丢弃，仅用于诊断丢包率。
 */
let cursorSeq = 0;
const nextCursorSeq = () => cursorSeq++;

/**
 * 最近一次发送的远端坐标，供 control 通道的 mouseDown/Up/Wheel 校准位置使用。
 * 当没有 mouseMove 时（如初始 click），回退到 0/0；receiver 端会做 clamp。
 */
let lastSentX = 0;
let lastSentY = 0;

const setLastSent = (x: number, y: number) => {
	lastSentX = x;
	lastSentY = y;
};

/**
 * 判断数据通道是否可写。
 */
const canSend = (
	dataChannel: RTCDataChannel | null,
): dataChannel is RTCDataChannel =>
	!!dataChannel && dataChannel.readyState === "open";

/**
 * 计算视频内容在容器内的实际渲染尺寸与偏移，处理 object-fit: contain 引入的黑边。
 * @param videoElement 当前绑定事件的视频元素
 */
const getRenderedVideoMetrics = (videoElement: HTMLVideoElement) => {
	if (!videoElement) return null;

	// 必须等待视频元数据加载，否则无法正确计算比率
	if (!videoElement.videoWidth || !videoElement.videoHeight) {
		return null;
	}

	const containerWidth = videoElement.clientWidth;
	const containerHeight = videoElement.clientHeight;
	if (!containerWidth || !containerHeight) return null;

	const intrinsicWidth = videoElement.videoWidth;
	const intrinsicHeight = videoElement.videoHeight;

	const containerRatio = containerWidth / containerHeight;
	const videoRatio = intrinsicWidth / intrinsicHeight;

	let renderWidth = containerWidth;
	let renderHeight = containerHeight;

	if (videoRatio > containerRatio) {
		renderWidth = containerWidth;
		renderHeight = containerWidth / videoRatio;
	} else {
		renderHeight = containerHeight;
		renderWidth = containerHeight * videoRatio;
	}

	const paddingX = (containerWidth - renderWidth) / 2;
	const paddingY = (containerHeight - renderHeight) / 2;

	return { renderWidth, renderHeight, paddingX, paddingY };
};

/**
 * 把 video 上的鼠标事件换算为远端物理像素坐标。
 * @returns 当前事件对应的远端 `{x,y}`，若信息不全则返回 null。
 */
const computeRemoteCoords = (
	event: MouseEvent,
): { x: number; y: number } | null => {
	const target = event.currentTarget as HTMLVideoElement | null;
	if (!target) return null;

	const metrics = getRenderedVideoMetrics(target);
	if (!metrics) return null;

	const { width: remoteWidth, height: remoteHeight } =
		useDeviceStore.getState().screenResolution;
	if (!remoteWidth || !remoteHeight) return null;

	const rect = target.getBoundingClientRect();
	const clientX = event.clientX - rect.left;
	const clientY = event.clientY - rect.top;

	const ratioX = (clientX - metrics.paddingX) / metrics.renderWidth;
	const ratioY = (clientY - metrics.paddingY) / metrics.renderHeight;

	const finalX = Math.round(Math.min(Math.max(ratioX, 0), 1) * remoteWidth);
	const finalY = Math.round(Math.min(Math.max(ratioY, 0), 1) * remoteHeight);

	return { x: finalX, y: finalY };
};

/**
 * W3C `MouseEvent.button` → 协议枚举。
 */
const mapButton = (button: number): "left" | "right" | "middle" | null => {
	if (button === 0) return "left";
	if (button === 1) return "middle";
	if (button === 2) return "right";
	return null;
};

/**
 * 鼠标移动事件处理。走 cursor 通道，携带 `seq`。
 * 同时更新缓存的最新坐标，供后续 mouseDown/Up/Wheel 校准位置使用。
 * @param cursorChannel cursor 通道
 * @param event
 */
export function mouseMoveEventHandler(
	cursorChannel: RTCDataChannel | null,
	event: MouseEvent,
) {
	if (!canSend(cursorChannel)) return;

	const coords = computeRemoteCoords(event);
	if (!coords) return;

	setLastSent(coords.x, coords.y);

	const payload = {
		v: PROTOCOL_VERSION,
		type: "mouseMove" as const,
		x: coords.x,
		y: coords.y,
		seq: nextCursorSeq(),
		t: Date.now(),
	};
	if (IS_DEV) console.log("[remote-input → cursor]", payload);
	try {
		cursorChannel.send(JSON.stringify(payload));
	} catch (error) {
		console.warn("[mouse] mouseMove send failed:", error);
	}
}

/**
 * PointerLock 下的相对位移事件处理。走 cursor 通道，携带累计 dx/dy 与 seq。
 * 协议 §7.1 要求 sender 在本地做聚合，输出频率上限 60Hz；这里仅负责单条编码与发送，
 * 调用方应自行节流。
 * @param cursorChannel cursor 通道
 * @param dx
 * @param dy
 */
export const mouseMoveRelativeEventHandler = (
	cursorChannel: RTCDataChannel | null,
	dx: number,
	dy: number,
) => {
	if (!canSend(cursorChannel)) return;
	// 协议 §5.1 capability gating：双方都声明 relativeMouse 才允许发送
	if (!hasNegotiatedCapability("relativeMouse")) return;
	const payload = {
		v: PROTOCOL_VERSION,
		type: "mouseMoveRelative" as const,
		dx,
		dy,
		seq: nextCursorSeq(),
		t: Date.now(),
	};
	if (IS_DEV) console.log("[remote-input → cursor]", payload);
	try {
		cursorChannel.send(JSON.stringify(payload));
	} catch (error) {
		console.warn("[mouse] mouseMoveRelative send failed:", error);
	}
};

/**
 * 更新当前远程设备的分辨率数据。
 * 实际计算时直接从 Store 读取最新值，保持无状态；这里仅打 log 确认更新。
 */
export const updateDeviceResolution = () => {
	const { width, height } = useDeviceStore.getState().screenResolution;
	console.log("[Mouse] Device resolution updated:", { width, height });
};

/**
 * 60fps 节流的 mouseMove handler，避免高频 pointermove 撑爆 cursor 通道。
 */
export const throttledMouseMoveEventHandler = throttle(
	mouseMoveEventHandler,
	16,
);

/**
 * 处理 mousedown / mouseup，转协议 §7.1 `mouseDown` / `mouseUp` 消息。
 *
 * 协议要求每条边沿消息携带事件发生时的 `x/y`，receiver 在执行 setMouseButton 之前
 * 先 `movePointer(x, y)` 校准位置，避免跨通道乱序导致的"点击落在旧位置"。
 *
 * @param controlChannel control 通道
 * @param event
 */
export const mouseButtonEventHandler = (
	controlChannel: RTCDataChannel | null,
	event: MouseEvent,
) => {
	if (!canSend(controlChannel)) return;

	const button = mapButton(event.button);
	if (!button) return;

	const coords = computeRemoteCoords(event) ?? {
		x: lastSentX,
		y: lastSentY,
	};
	setLastSent(coords.x, coords.y);

	const type =
		event.type === "mousedown" || event.type === "pointerdown"
			? "mouseDown"
			: "mouseUp";

	const payload = {
		v: PROTOCOL_VERSION,
		type,
		button,
		x: coords.x,
		y: coords.y,
		t: Date.now(),
	};
	if (IS_DEV) console.log("[remote-input → control]", payload);
	try {
		controlChannel.send(JSON.stringify(payload));
	} catch (error) {
		console.warn("[mouse] mouseButton send failed:", error);
	}
};

/**
 * 处理 wheel 事件，转协议 §7.1 `mouseWheel` 消息。
 *
 * `deltaMode` 对齐 W3C `WheelEvent.deltaMode`（0=pixel、1=line、2=page）。
 * @param controlChannel control 通道
 * @param event
 */
export const mouseWheelEventHandler = (
	controlChannel: RTCDataChannel | null,
	event: WheelEvent,
) => {
	if (!canSend(controlChannel)) return;

	// 协议 §5.1 capability gating：双方都声明 wheel 才允许发送。
	// 未协商时不发，但仍 preventDefault 阻止页面在远控视口内滚动。
	event.preventDefault();
	if (!hasNegotiatedCapability("wheel")) return;

	const coords = computeRemoteCoords(event) ?? {
		x: lastSentX,
		y: lastSentY,
	};
	setLastSent(coords.x, coords.y);

	const payload = {
		v: PROTOCOL_VERSION,
		type: "mouseWheel" as const,
		deltaX: event.deltaX,
		deltaY: event.deltaY,
		deltaMode: event.deltaMode as 0 | 1 | 2,
		x: coords.x,
		y: coords.y,
		t: Date.now(),
	};
	if (IS_DEV) console.log("[remote-input → control]", payload);
	try {
		controlChannel.send(JSON.stringify(payload));
	} catch (error) {
		console.warn("[mouse] mouseWheel send failed:", error);
	}
};

/**
 * 处理右键 contextmenu：仅阻止本地浏览器菜单弹出。
 * 实际右键按下/抬起的协议消息由 mousedown / mouseup 处理（按钮 button=2 → 'right'）。
 */
export const preventContextMenuHandler = (event: MouseEvent) => {
	event.preventDefault();
};
